import React, { useState, useEffect, useCallback } from "react";
import {
  Layout,
  Card,
  Table,
  Button,
  Space,
  Tag,
  Typography,
  Row,
  Col,
  Statistic,
  Input,
  Modal,
  Timeline,
  Descriptions,
  Badge,
  Divider,
  Form,
  Select,
} from "antd";
import {
  FaTicketAlt,
  FaSearch,
  FaEye,
  FaUserCheck,
  FaClock,
  FaCheckCircle,
  FaPauseCircle,
  FaExclamationTriangle,
  FaEdit,
  FaTimesCircle,
  FaPlayCircle,
} from "react-icons/fa";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Api from "../api/Api";

const { Content } = Layout;
const { Title } = Typography;
const { TextArea } = Input;
const { Option } = Select;

/* ─────────────────────────────────────────────────────────────────────────────
   HELPERS
───────────────────────────────────────────────────────────────────────────── */
const formatDate = (str) => {
  if (!str) return "—";
  try {
    return new Date(str).toLocaleString("pt-PT", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return str;
  }
};

const hmsToSeconds = (hms) => {
  if (!hms) return 0;
  try {
    const parts = hms.split(":").map(Number);
    return (parts[0] || 0) * 3600 + (parts[1] || 0) * 60 + (parts[2] || 0);
  } catch {
    return 0;
  }
};

const secondsToHms = (total) => {
  if (total < 0) total = 0;
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  return `${String(h).padStart(2, "0")}h:${String(m).padStart(
    2,
    "0"
  )}min:${String(s).padStart(2, "0")}s`;
};

const calcDuracao = (abertura, fecho, tempoSuspenso) => {
  if (!abertura || !fecho) return null;
  try {
    const bruto = Math.floor((new Date(fecho) - new Date(abertura)) / 1000);
    if (bruto < 0) return null;
    const suspenso = hmsToSeconds(tempoSuspenso);
    return secondsToHms(bruto - suspenso);
  } catch {
    return null;
  }
};

/* ─────────────────────────────────────────────────────────────────────────────
   MODAL DE DETALHE
───────────────────────────────────────────────────────────────────────────── */
const TicketDetailModal = ({
  ticket,
  open,
  onClose,
  onAssign,
  assigning,
  onStatusChanged,
  userStored,
}) => {
  const [responsaveis, setResponsaveis] = useState([]);
  const [atualizacoes, setAtualizacoes] = useState([]);
  const [loadingModal, setLoadingModal] = useState(false);
  const [newUpd, setNewUpd] = useState("");
  const [addingUpd, setAddingUpd] = useState(false);
  const [changingStatus, setChangingStatus] = useState(false);
  const [allStatus, setAllStatus] = useState([]);

  const isResponsavel = responsaveis.some(
    (r) =>
      r.nome?.trim().toLowerCase() === userStored?.nome?.trim().toLowerCase()
  );

  const statusUp = ticket?.status?.toUpperCase();
  const isAberto = statusUp === "ABERTO";
  const isSuspenso = statusUp === "SUSPENSO";
  const isFechado = statusUp === "FECHADO";

  useEffect(() => {
    if (!ticket || !open) return;
    const load = async () => {
      setLoadingModal(true);
      setResponsaveis([]);
      setAtualizacoes([]);
      try {
        const [resps, upds, statuses] = await Promise.all([
          Api.ListResponsavel(ticket.id).catch(() => []),
          Api.ListAtualizacao(ticket.id).catch(() => []),
          Api.ListStatus().catch(() => []),
        ]);
        setResponsaveis(Array.isArray(resps) ? resps : []);
        setAtualizacoes(Array.isArray(upds) ? upds : []);
        setAllStatus(Array.isArray(statuses) ? statuses : []);
      } finally {
        setLoadingModal(false);
      }
    };
    load();
  }, [ticket, open]);

  const handleAddUpd = async () => {
    if (!newUpd.trim()) return;
    try {
      setAddingUpd(true);
      await Api.AddAtualizacao(newUpd.trim(), userStored.nome, ticket.id);
      toast.success("Actualização adicionada!");
      setNewUpd("");
      const upds = await Api.ListAtualizacao(ticket.id).catch(() => []);
      setAtualizacoes(Array.isArray(upds) ? upds : []);
    } catch {
      toast.error("Erro ao adicionar actualização.");
    } finally {
      setAddingUpd(false);
    }
  };

  const handleChangeStatus = async (descricao) => {
    const found = allStatus.find(
      (s) => s.descricao?.toUpperCase() === descricao.toUpperCase()
    );
    if (!found) {
      toast.error("Status não encontrado.");
      return;
    }
    try {
      setChangingStatus(true);
      await Api.AlterTicket({ id: ticket.id, id_status: found.id });
      toast.success(`Ticket marcado como ${descricao}!`);
      onStatusChanged(ticket.id, found.descricao);
      onClose();
    } catch {
      toast.error("Erro ao alterar o status.");
    } finally {
      setChangingStatus(false);
    }
  };

  if (!ticket) return null;

  const duracao = calcDuracao(
    ticket.abertura,
    ticket.fecho,
    ticket.tempo_suspenso
  );

  const getStatusTag = (status) => {
    const s = status?.toUpperCase();
    if (s === "ABERTO") return <Tag color="blue">ABERTO</Tag>;
    if (s === "SUSPENSO") return <Tag color="orange">SUSPENSO</Tag>;
    if (s === "FECHADO") return <Tag color="green">FECHADO</Tag>;
    return <Tag>{status}</Tag>;
  };

  const getCriticidadeTag = (crit) => {
    const c = crit?.toLowerCase();
    if (c === "alta") return <Tag color="red">ALTA</Tag>;
    if (c === "média" || c === "media") return <Tag color="orange">MÉDIA</Tag>;
    return <Tag color="green">BAIXA</Tag>;
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      width={800}
      footer={[
        <Button key="close" onClick={onClose}>
          Fechar
        </Button>,
        isAberto && !isResponsavel && (
          <Button
            key="assign"
            type="primary"
            icon={<FaUserCheck />}
            onClick={() => onAssign(ticket)}
            loading={assigning}
          >
            Assumir Responsabilidade
          </Button>
        ),
      ]}
      title={
        <Space>
          <FaTicketAlt style={{ color: "#1677ff" }} />
          <span>Detalhes do Ticket - {ticket.numero}</span>
        </Space>
      }
    >
      {loadingModal ? (
        <div style={{ textAlign: "center", padding: "40px 0" }}>
          <Space>
            <span>A carregar dados...</span>
          </Space>
        </div>
      ) : (
        <div>
          <Descriptions bordered size="small" column={2}>
            <Descriptions.Item label="Número" span={1}>
              <strong>{ticket.numero}</strong>
            </Descriptions.Item>
            <Descriptions.Item label="Status" span={1}>
              {getStatusTag(ticket.status)}
            </Descriptions.Item>
            <Descriptions.Item label="Ocorrência" span={2}>
              {ticket.ocorrencia}
            </Descriptions.Item>
            <Descriptions.Item label="Utente">
              {ticket.utente || "—"}
            </Descriptions.Item>
            <Descriptions.Item label="Criticidade">
              {getCriticidadeTag(ticket.criticidade)}
            </Descriptions.Item>
            <Descriptions.Item label="Direcção">
              {ticket.direcao || "—"}
            </Descriptions.Item>
            <Descriptions.Item label="Abertura">
              {formatDate(ticket.abertura)}
            </Descriptions.Item>
            <Descriptions.Item label="Fecho">
              {formatDate(ticket.fecho)}
            </Descriptions.Item>
            {ticket.tempo_suspenso && (
              <Descriptions.Item label="Tempo Suspenso">
                {ticket.tempo_suspenso}
              </Descriptions.Item>
            )}
            {duracao && (
              <Descriptions.Item label="Duração Efectiva">
                {duracao}
              </Descriptions.Item>
            )}
            <Descriptions.Item label="Descrição" span={2}>
              {ticket.descricao || "Sem descrição disponível."}
            </Descriptions.Item>
          </Descriptions>

          <Divider orientation="left">Responsáveis</Divider>
          {responsaveis.length === 0 ? (
            <p style={{ color: "#999", fontStyle: "italic" }}>
              Sem responsáveis atribuídos.
            </p>
          ) : (
            <Space wrap>
              {responsaveis.map((r, i) => {
                const isMe =
                  r.nome?.trim().toLowerCase() ===
                  userStored?.nome?.trim().toLowerCase();
                return (
                  <Tag
                    key={i}
                    color={isMe ? "blue" : "purple"}
                    icon={<FaUserCheck />}
                  >
                    {r.nome}
                    {isMe ? " (eu)" : ""}
                  </Tag>
                );
              })}
            </Space>
          )}

          {isResponsavel && !isFechado && (
            <>
              <Divider orientation="left">Alterar Status</Divider>
              <Space>
                {isAberto && (
                  <>
                    <Button
                      icon={<FaPauseCircle />}
                      onClick={() => handleChangeStatus("SUSPENSO")}
                      loading={changingStatus}
                    >
                      Suspender
                    </Button>
                    <Button
                      type="primary"
                      icon={<FaCheckCircle />}
                      onClick={() => handleChangeStatus("FECHADO")}
                      loading={changingStatus}
                      style={{ background: "#52c41a", borderColor: "#52c41a" }}
                    >
                      Fechar Ticket
                    </Button>
                  </>
                )}
                {isSuspenso && (
                  <Button
                    type="primary"
                    icon={<FaPlayCircle />}
                    onClick={() => handleChangeStatus("ABERTO")}
                    loading={changingStatus}
                  >
                    Reabrir Ticket
                  </Button>
                )}
              </Space>
            </>
          )}

          <Divider orientation="left">Atualizações</Divider>
          {atualizacoes.length === 0 ? (
            <p style={{ color: "#999", fontStyle: "italic" }}>
              Sem atualizações registadas.
            </p>
          ) : (
            <Timeline
              items={atualizacoes.map((u) => ({
                children: (
                  <div>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        marginBottom: 4,
                      }}
                    >
                      <strong style={{ color: "#1677ff" }}>{u.usuario}</strong>
                      <span style={{ fontSize: 12, color: "#999" }}>
                        {formatDate(u.created_at)}
                      </span>
                    </div>
                    <div>{u.descricao}</div>
                  </div>
                ),
              }))}
            />
          )}

          {isResponsavel && isAberto && (
            <>
              <Divider orientation="left">Adicionar Actualização</Divider>
              <Space.Compact style={{ width: "100%" }} direction="vertical">
                <TextArea
                  rows={3}
                  placeholder="Descreva a actualização do ticket..."
                  value={newUpd}
                  onChange={(e) => setNewUpd(e.target.value)}
                />
                <Button
                  type="primary"
                  icon={<FaEdit />}
                  onClick={handleAddUpd}
                  loading={addingUpd}
                  disabled={!newUpd.trim()}
                  style={{ marginTop: 8 }}
                >
                  Guardar Actualização
                </Button>
              </Space.Compact>
            </>
          )}
        </div>
      )}
    </Modal>
  );
};

/* ─────────────────────────────────────────────────────────────────────────────
   TICKET PAGE
───────────────────────────────────────────────────────────────────────────── */
const Ticket = () => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("TODOS");
  const [selected, setSelected] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [respCache, setRespCache] = useState({});

  const userStored = JSON.parse(localStorage.getItem("user")) || {};

  const sms_success = (m) => toast.success(m);
  const sms_error = (m) => toast.error(m);

  useEffect(() => {
    document.title = "TICKETS";
  }, []);

  const loadTickets = useCallback(async () => {
    try {
      setLoading(true);
      const payload = { id_direcao: userStored.direcao };
      const response = await Api.ListTicket(payload);
      const list = Array.isArray(response) ? response : [];
      setTickets(list);

      const entries = await Promise.all(
        list.map(async (t) => {
          try {
            const resps = await Api.ListResponsavel(t.id);
            return [t.id, Array.isArray(resps) ? resps : []];
          } catch {
            return [t.id, []];
          }
        })
      );
      setRespCache(Object.fromEntries(entries));
    } catch {
      sms_error("Erro ao carregar tickets.");
      setTickets([]);
    } finally {
      setLoading(false);
    }
  }, [userStored.direcao]);

  useEffect(() => {
    loadTickets();
  }, [loadTickets]);

  const openDetail = (t) => {
    setSelected(t);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setSelected(null);
  };

  const handleAssign = async (t) => {
    try {
      setAssigning(true);
      await Api.AssumirTicket({ id_ticket: t.id, id_user: userStored.id });
      sms_success(`Assumiste o ticket ${t.numero}!`);
      setRespCache((prev) => ({
        ...prev,
        [t.id]: [
          ...(prev[t.id] || []),
          { nome: userStored.nome, id_ticket: t.id },
        ],
      }));
      closeModal();
    } catch {
      sms_error("Erro ao assumir o ticket.");
    } finally {
      setAssigning(false);
    }
  };

  const handleStatusChanged = (ticketId, novoStatus) => {
    setTickets((prev) =>
      prev.map((t) => (t.id === ticketId ? { ...t, status: novoStatus } : t))
    );
  };

  const countAberto = tickets.filter(
    (t) => t.status?.toUpperCase() === "ABERTO"
  ).length;
  const countFechado = tickets.filter(
    (t) => t.status?.toUpperCase() === "FECHADO"
  ).length;
  const countSuspenso = tickets.filter(
    (t) => t.status?.toUpperCase() === "SUSPENSO"
  ).length;

  const filtered = tickets.filter((t) => {
    const matchFilter =
      filter === "TODOS" || t.status?.toUpperCase() === filter;
    const q = search.toLowerCase();
    const matchSearch =
      !q ||
      t.numero?.toLowerCase().includes(q) ||
      t.ocorrencia?.toLowerCase().includes(q) ||
      t.utente?.toLowerCase().includes(q) ||
      t.criticidade?.toLowerCase().includes(q);
    return matchFilter && matchSearch;
  });

  const columns = [
    {
      title: "Número",
      dataIndex: "numero",
      key: "numero",
      render: (num) => <strong style={{ color: "#1677ff" }}>{num}</strong>,
    },
    {
      title: "Ocorrência",
      dataIndex: "ocorrencia",
      key: "ocorrencia",
      ellipsis: true,
    },
    {
      title: "Utente",
      dataIndex: "utente",
      key: "utente",
    },
    {
      title: "Criticidade",
      dataIndex: "criticidade",
      key: "criticidade",
      render: (crit) => {
        const c = crit?.toLowerCase();
        if (c === "alta") return <Tag color="red">ALTA</Tag>;
        if (c === "média" || c === "media")
          return <Tag color="orange">MÉDIA</Tag>;
        return <Tag color="green">BAIXA</Tag>;
      },
    },
    {
      title: "Abertura",
      dataIndex: "abertura",
      key: "abertura",
      render: (t) => formatDate(t),
    },
    {
      title: "Fecho",
      dataIndex: "fecho",
      key: "fecho",
      render: (fecho, record) => {
        const duracao = calcDuracao(
          record.abertura,
          record.fecho,
          record.tempo_suspenso
        );
        if (duracao) {
          return (
            <div>
              <div>{duracao}</div>
              {record.tempo_suspenso && (
                <div style={{ fontSize: 11, color: "#faad14" }}>
                  Suspenso: {record.tempo_suspenso}
                </div>
              )}
            </div>
          );
        }
        return formatDate(fecho);
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => {
        const s = status?.toUpperCase();
        if (s === "ABERTO") return <Tag color="blue">ABERTO</Tag>;
        if (s === "SUSPENSO") return <Tag color="orange">SUSPENSO</Tag>;
        if (s === "FECHADO") return <Tag color="green">FECHADO</Tag>;
        return <Tag>{status}</Tag>;
      },
    },
    {
      title: "Acções",
      key: "actions",
      render: (_, record) => {
        const statusUp = record.status?.toUpperCase();
        const isAberto = statusUp === "ABERTO";
        const resps = respCache[record.id] || [];
        const jaResp = resps.some(
          (r) =>
            r.nome?.trim().toLowerCase() ===
            userStored?.nome?.trim().toLowerCase()
        );

        return (
          <Space>
            {isAberto && !jaResp && (
              <Button
                size="small"
                icon={<FaUserCheck />}
                onClick={() => handleAssign(record)}
                loading={assigning}
              >
                Assumir
              </Button>
            )}
            <Button
              size="small"
              type="primary"
              icon={<FaEye />}
              onClick={() => openDetail(record)}
            >
              Detalhes
            </Button>
          </Space>
        );
      },
    },
  ];

  return (
    <Layout>
      <Content style={{ padding: 24 }}>
        <ToastContainer />

        <Card
          title={
            <Space>
              <FaTicketAlt style={{ fontSize: 22, color: "#1677ff" }} />
              <Title level={4} style={{ margin: 0 }}>
                Gestão de Tickets
              </Title>
            </Space>
          }
        >
          {/* RESUMO */}
          <Row gutter={16} style={{ marginBottom: 24 }}>
            <Col xs={24} sm={12} md={6}>
              <Card size="small" hoverable>
                <Statistic
                  title="Total de Tickets"
                  value={tickets.length}
                  prefix={<FaTicketAlt />}
                  valueStyle={{ color: "#1677ff" }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Card size="small" hoverable>
                <Statistic
                  title="Abertos"
                  value={countAberto}
                  prefix={<FaExclamationTriangle />}
                  valueStyle={{ color: "#1890ff" }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Card size="small" hoverable>
                <Statistic
                  title="Suspensos"
                  value={countSuspenso}
                  prefix={<FaPauseCircle />}
                  valueStyle={{ color: "#faad14" }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Card size="small" hoverable>
                <Statistic
                  title="Fechados"
                  value={countFechado}
                  prefix={<FaCheckCircle />}
                  valueStyle={{ color: "#52c41a" }}
                />
              </Card>
            </Col>
          </Row>

          {/* FILTROS */}
          <Row gutter={16} style={{ marginBottom: 16 }}>
            <Col xs={24} md={12}>
              <Input
                prefix={<FaSearch />}
                placeholder="Pesquisar por número, ocorrência, utente..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                allowClear
              />
            </Col>
            <Col xs={24} md={12}>
              <Select
                style={{ width: "100%" }}
                value={filter}
                onChange={setFilter}
              >
                <Option value="TODOS">Todos</Option>
                <Option value="ABERTO">Abertos</Option>
                <Option value="SUSPENSO">Suspensos</Option>
                <Option value="FECHADO">Fechados</Option>
              </Select>
            </Col>
          </Row>

          {/* TABELA */}
          <Table
            columns={columns}
            dataSource={filtered}
            rowKey="id"
            loading={loading}
            pagination={{ pageSize: 10 }}
            locale={{ emptyText: "Nenhum ticket encontrado" }}
          />
        </Card>

        {/* MODAL */}
        <TicketDetailModal
          ticket={selected}
          open={modalOpen}
          onClose={closeModal}
          onAssign={handleAssign}
          assigning={assigning}
          onStatusChanged={handleStatusChanged}
          userStored={userStored}
        />
      </Content>
    </Layout>
  );
};

export default Ticket;